// The MIT License
//
// Copyright (c) 2018 Google, Inc.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import * as React from 'react';
import classnames from 'classnames';

import { MDCDismissibleSideSheetFoundation, MDCModalSideSheetFoundation, util } from './MDCSideSheet';
import { MDCListFoundation } from '@material/list';
import './SideSheet.scss';

import SideSheetContent from './Content';

// import SideSheetAppContent from './AppContent';

const { cssClasses: listCssClasses } = MDCListFoundation;
//const style = require("./SideSheet.scss");

export interface ISideSheetProps {
  className?: string;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onTransitionEnd?: (event: React.TransitionEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  tag?: string;
  dismissible?: boolean;
  modal?: boolean;
}

interface ISideSheetState {
  classList: Set<string>;
}

class SideSheet extends React.Component<ISideSheetProps & React.HTMLProps<HTMLElement>, ISideSheetState> {
  private previousFocus_!: HTMLElement | null;
  private foundation_!: MDCDismissibleSideSheetFoundation | MDCModalSideSheetFoundation;
  private focusTrap_;
  private sidesheetElement_: React.RefObject<HTMLDivElement> = React.createRef();
  //@ts-ignore

  constructor(props) {
    super(props);
    this.state = {
      classList: new Set()
    };
  }

  private static defaultProps = {
    className: '',
    children: null,
    open: false,
    onOpen: () => {},
    onClose: () => {},
    onTransitionEnd: () => {},
    onKeyDown: () => {},
    tag: 'aside',
    dismissible: false,
    modal: false
  };

  public componentDidMount() {
    const { open } = this.props;
    this.initFoundation();
    if (open && this.foundation_) {
      this.foundation_.open();
    }
  }

  private initFoundation = () => {
    const { dismissible, modal } = this.props;
    if (this.foundation_) {
      this.foundation_.destroy();
    }
    if (dismissible) {
      this.foundation_ = new MDCDismissibleSideSheetFoundation(this.adapter);
      // @ts-ignore
      this.foundation_.init();
    } else if (modal) {
      this.initializeFocusTrap();
      this.foundation_ = new MDCModalSideSheetFoundation(this.adapter);
      // @ts-ignore */
      this.foundation_.init();
    }
  };

  public componentDidUpdate(prevProps) {
    const { dismissible, modal, open } = this.props;
    const changedToModal = prevProps.modal !== this.props.modal;
    const changedToDismissible = prevProps.dismissible !== this.props.dismissible;
    if (!dismissible && !modal) return;
    if (changedToModal || changedToDismissible) {
      this.initFoundation();
    }
    if (open !== prevProps.open) {
      open ? this.foundation_.open() : this.foundation_.close();
    }
  }

  public componentWillUnmount() {
    if (!this.foundation_) return;
    this.foundation_.destroy();
  }

  private initializeFocusTrap = () => {
    if (this.sidesheetElement_.current !== null) {
      this.focusTrap_ = util.createFocusTrapInstance(this.sidesheetElement_.current);
    }
  };

  get classes() {
    const { classList } = this.state;
    const { className, dismissible, modal } = this.props;
    const openedClass = this.props.open ? 'side-sheet-open' : '';
    return classnames(
      'mdc-side-sheet',
      Array.from(classList),
      className,
      {
        'mdc-side-sheet--dismissible': dismissible,
        'mdc-side-sheet--modal': modal
      },
      openedClass
    );
  }

  get adapter() {
    return {
      addClass: className => {
        const { classList } = this.state;
        classList.add(className);
        //@ts-ignore
        this.setState({ classList });
      },
      removeClass: className => {
        const { classList } = this.state;
        classList.delete(className);
        //@ts-ignore
        this.setState({ classList });
      },
      hasClass: className => this.classes.split(' ').includes(className),
      elementHasClass: (element, className) => element.classList.contains(className),
      saveFocus: () => {
        this.previousFocus_ = document.activeElement as HTMLElement | null;
      },
      restoreFocus: () => {
        const hasPreviousFocus = this.previousFocus_ && this.previousFocus_.focus;
        const sidesheetElement = this.sidesheetElement_ && this.sidesheetElement_.current;
        if (sidesheetElement && hasPreviousFocus && sidesheetElement.contains(document.activeElement)) {
          this.previousFocus_!.focus();
        }
      },
      focusActiveNavigationItem: () => {
        const sidesheetElement = this.sidesheetElement_ && this.sidesheetElement_.current;
        if (!sidesheetElement) return;
        const activeNavItemEl = sidesheetElement.querySelector(`.${listCssClasses.LIST_ITEM_ACTIVATED_CLASS}`) as HTMLElement | null;
        if (activeNavItemEl) {
          activeNavItemEl.focus();
        }
      },
      notifyClose: this.props.onClose,
      notifyOpen: this.props.onOpen,
      trapFocus: () => {
        if (this.focusTrap_) {
          this.focusTrap_.activate();
        }
      },
      releaseFocus: () => {
        if (this.focusTrap_) {
          this.focusTrap_.deactivate();
        }
      }
    };
  }

  private handleKeyDown = evt => {
    // @ts-ignore */
    this.props.onKeyDown(evt);
    if (!this.foundation_) return;
    this.foundation_.handleKeydown(evt);
  };

  private handleTransitionEnd = evt => {
    // @ts-ignore */
    this.props.onTransitionEnd(evt);
    if (!this.foundation_) return;
    this.foundation_.handleTransitionEnd(evt);
  };

  public render() {
    const {
      /* eslint-disable no-unused-vars */
      onClose,
      onOpen,
      onKeyDown,
      onTransitionEnd,
      dismissible,
      children,
      className,
      /* eslint-enable no-unused-vars */
      tag: Tag,
      modal,
      ...otherProps
    } = this.props;

    return (
      <React.Fragment>
        {/* https://github.com/Microsoft/TypeScript/issues/28892 
          // @ts-ignore */}
        <Tag className={this.classes} id="mdc-side-sheet" ref={this.sidesheetElement_} onKeyDown={evt => this.handleKeyDown(evt)} onTransitionEnd={evt => this.handleTransitionEnd(evt)} {...otherProps}>
          {children}
        </Tag>
        {modal ? this.renderScrim() : null}
      </React.Fragment>
    );
  }
  private renderScrim() {
    return (
      <div
        className="mdc-side-sheet-scrim"
        onClick={() => {
          // @ts-ignore
          return this.foundation_.handleScrimClick();
        }}
      />
    );
  }
}

export default SideSheet;
export {
  SideSheetContent
  // SideSheetAppContent
};
